import { useState, useRef } from 'react';
import type { CompetitorEntry } from '../hooks/useOnboardingState';
import { TONE_OPTIONS, ARCHETYPE_OPTIONS, PLATFORM_PRIORITY_ORDER, PLATFORM_TYPE_OPTIONS } from '../hooks/useOnboardingState';

interface Step2BrandCommunicationProps {
  selectedTones: string[];
  selectedArchetypes: string[];
  platformPriority: string[];
  competitors: CompetitorEntry[];
  onToggleTone: (tone: string) => void;
  onToggleArchetype: (arch: string) => void;
  onMovePlatformPriority: (fromIndex: number, toIndex: number) => void;
  onUpdateCompetitor: (index: number, field: keyof CompetitorEntry, value: string) => void;
  onAddCompetitor: () => void;
  onRemoveCompetitor: (index: number) => void;
  onBlurCompetitor: (index: number, field: keyof CompetitorEntry, value: string) => void;
  isStep2Valid: boolean;
  fieldErrors?: Record<string, string>;
}

export default function Step2BrandCommunication({
  selectedTones,
  selectedArchetypes,
  platformPriority,
  competitors,
  onToggleTone,
  onToggleArchetype,
  onMovePlatformPriority,
  onUpdateCompetitor,
  onAddCompetitor,
  onRemoveCompetitor,
  onBlurCompetitor,
  isStep2Valid,
  fieldErrors = {},
}: Step2BrandCommunicationProps) {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragItem = useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    dragItem.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragItem.current !== null && dragItem.current !== index) {
      onMovePlatformPriority(dragItem.current, index);
    }
    dragItem.current = null;
    setDragOverIndex(null);
  };

  const getPlatformInfo = (id: string) => PLATFORM_PRIORITY_ORDER.find((p) => p.id === id);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center mb-2">
        {/* <h2 className="text-xl font-bold text-gray-900 mb-1">Brand Communication Setup</h2> */}
        <p className="text-sm text-gray-500">Define your voice, personality matrix, and competitive landscape</p>
      </div>

      {/* Section 1: Communication Tone Matrix */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 bg-teal-50 rounded-lg flex items-center justify-center">
            <i className="ri-mic-line text-teal-600 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Communication Tone Matrix</h3>
          <span className="text-[10px] text-gray-400 ml-auto">Multi-select (Max 3)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {TONE_OPTIONS.map((tone) => {
            const selected = selectedTones.includes(tone);
            const isMaxReached = selectedTones.length >= 3;
            const disabled = isMaxReached && !selected;
            return (
              <button
                key={tone}
                onClick={() => onToggleTone(tone)}
                disabled={disabled}
                className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap ${selected
                    ? 'bg-teal-500 text-white border-teal-500 shadow-sm cursor-pointer'
                    : disabled
                      ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed opacity-50'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300 hover:text-teal-700 cursor-pointer'
                  }`}
              >
                {selected && <i className="ri-check-line mr-1 text-xs"></i>}
                {tone}
              </button>
            );
          })}
        </div>
        {fieldErrors?.communication_tones && (
          <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {fieldErrors.communication_tones}
          </p>
        )}
      </div>

      {/* Section 2: Brand Personality Archetypes */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 bg-violet-50 rounded-lg flex items-center justify-center">
            <i className="ri-user-star-line text-violet-600 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Brand Personality Archetypes</h3>
          <span className="text-[10px] text-gray-400 ml-auto">Multi-select (Max 3)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {ARCHETYPE_OPTIONS.map((arch) => {
            const selected = selectedArchetypes.includes(arch);
            const isMaxReached = selectedArchetypes.length >= 3;
            const disabled = isMaxReached && !selected;
            return (
              <button
                key={arch}
                onClick={() => onToggleArchetype(arch)}
                disabled={disabled}
                className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap ${selected
                    ? 'bg-violet-600 text-white border-violet-600 shadow-sm cursor-pointer'
                    : disabled
                      ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed opacity-50'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300 hover:text-violet-700 cursor-pointer'
                  }`}
              >
                {selected && <i className="ri-check-line mr-1 text-xs"></i>}
                {arch}
              </button>
            );
          })}
        </div>
        {fieldErrors?.brand_personalities && (
          <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {fieldErrors.brand_personalities}
          </p>
        )}
      </div>

      {/* Section 3: Platform Priority Ranking */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
            <i className="ri-drag-move-line text-amber-600 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Platform Priority Ranking</h3>
          <span className="text-[10px] text-gray-400 ml-auto">Drag to reorder</span>
        </div>
        <div className="space-y-2">
          {platformPriority
            .filter((pid) => {
              if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') return true;
              return pid === 'youtube';
            })
            .map((pid, index) => {
              const platform = getPlatformInfo(pid);
              if (!platform) return null;
              return (
              <div
                key={pid}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={() => setDragOverIndex(null)}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-move ${dragOverIndex === index
                    ? 'border-teal-300 bg-teal-50'
                    : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                  }`}
              >
                <div className="w-6 h-6 flex items-center justify-center text-gray-400">
                  <i className="ri-draggable text-lg"></i>
                </div>
                <span className="text-xs font-bold text-gray-400 w-5 text-center">{index + 1}</span>
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${index === 0
                      ? 'bg-teal-100 text-teal-600'
                      : index === 1
                        ? 'bg-emerald-100 text-emerald-600'
                        : index === 2
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-gray-100 text-gray-500'
                    }`}
                >
                  <i className={`${platform.icon} text-sm`}></i>
                </div>
                <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                {index === 0 && (
                  <span className="ml-auto text-[10px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold">
                    Primary
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {fieldErrors?.platform_priority_order && (
          <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {fieldErrors.platform_priority_order}
          </p>
        )}
      </div>

      {/* Section 4: Competitor Intelligence Grid */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 bg-teal-50 rounded-lg flex items-center justify-center">
            <i className="ri-spy-line text-teal-600 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Competitor Intelligence Grid</h3>
        </div>
        <div className="space-y-3">
          {competitors.map((comp, index) => (
            <div key={index} className="space-y-1">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
                <div className="flex flex-col">
                  <input
                    type="text"
                    value={comp.name}
                    onChange={(e) => onUpdateCompetitor(index, 'name', e.target.value)}
                    onBlur={(e) => onBlurCompetitor(index, 'name', e.target.value)}
                    placeholder="Competitor Name"
                    className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 transition-colors ${
                      fieldErrors[`competitors.${index}.name`]
                        ? 'border-red-400 focus:ring-red-400 bg-red-50'
                        : 'border-gray-200 focus:ring-teal-500'
                    }`}
                  />
                  {fieldErrors[`competitors.${index}.name`] && (
                    <span className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {fieldErrors[`competitors.${index}.name`]}
                    </span>
                  )}
                </div>

                <div className="flex flex-col">
                  <input
                    type="text"
                    value={comp.url}
                    onChange={(e) => onUpdateCompetitor(index, 'url', e.target.value)}
                    onBlur={(e) => onBlurCompetitor(index, 'url', e.target.value)}
                    placeholder="Social URL"
                    className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 transition-colors ${
                      fieldErrors[`competitors.${index}.url`]
                        ? 'border-red-400 focus:ring-red-400 bg-red-50'
                        : 'border-gray-200 focus:ring-teal-500'
                    }`}
                  />
                  {fieldErrors[`competitors.${index}.url`] && (
                    <span className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {fieldErrors[`competitors.${index}.url`]}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1 flex flex-col">
                    <select
                      value={comp.focus}
                      onChange={(e) => onUpdateCompetitor(index, 'focus', e.target.value)}
                      onBlur={(e) => onBlurCompetitor(index, 'focus', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 bg-white text-gray-600 transition-colors ${
                        fieldErrors[`competitors.${index}.focus`]
                          ? 'border-red-400 focus:ring-red-400 bg-red-50'
                          : 'border-gray-200 focus:ring-teal-500'
                      }`}
                    >
                      <option value="">Select Platform Type...</option>
                      {PLATFORM_TYPE_OPTIONS
                        .filter((opt) => {
                          if (opt === 'Twitter / X') return false;
                          if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') return true;
                          return opt === 'YouTube';
                        })
                        .map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                    </select>
                    {fieldErrors[`competitors.${index}.focus`] && (
                      <span className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {fieldErrors[`competitors.${index}.focus`]}
                      </span>
                    )}
                  </div>
                  {competitors.length > 1 && (
                    <button
                      onClick={() => onRemoveCompetitor(index)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={onAddCompetitor}
          disabled={!isStep2Valid}
          className={`mt-3 inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
            !isStep2Valid
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-teal-600 hover:text-teal-700 cursor-pointer'
          }`}
        >
          <i className="ri-add-line"></i>
          Add Competitor
        </button>
      </div>
    </div>
  );
}